/**
 * Jackpot API Service
 *
 * Centralized API client for all jackpot-related endpoints.
 * Uses the centralized URL resolver and HTTP utilities.
 *
 * Aligned with backend routes at serverless/api/routes/jackpot.py
 */

import { buildCloudApiUrl } from './urlResolver';
import { handleResponse, getAuthHeaders } from '../utils/httpUtils';
import type {
  JackpotPool,
  JackpotTickets,
  SpinResult,
  SpinAllResult,
  CheckinResponse,
  TicketPurchaseResponse,
} from '../types/jackpot';

/**
 * Jackpot API endpoints
 * Aligned with backend router at /api/v1/jackpot
 */
const ENDPOINTS = {
  pool: '/api/v1/jackpot/pool',
  checkin: '/api/v1/jackpot/checkin',
  tickets: '/api/v1/jackpot/tickets',
  purchaseTickets: '/api/v1/jackpot/tickets/purchase',
  spin: '/api/v1/jackpot/spin',
  spinAll: '/api/v1/jackpot/spin-all',
} as const;

/**
 * Get the current jackpot pool information
 * Public endpoint - no authentication required
 */
export async function getPool(): Promise<JackpotPool> {
  // No Content-Type header on GET: avoids unnecessary CORS preflight.
  // Content-Type: application/json triggers OPTIONS preflight per CORS spec,
  // which can fail on cold Cloud Run instances causing intermittent CORS errors.
  const response = await fetch(buildCloudApiUrl(ENDPOINTS.pool), {
    method: 'GET',
  });
  return handleResponse<JackpotPool>(response);
}

/**
 * Process daily check-in
 * Updates streak progress and may earn tickets
 * Requires authentication
 *
 * @param token - Firebase ID token
 */
export async function checkin(token: string): Promise<CheckinResponse> {
  const response = await fetch(buildCloudApiUrl(ENDPOINTS.checkin), {
    method: 'POST',
    headers: getAuthHeaders(token),
  });
  return handleResponse<CheckinResponse>(response);
}

/**
 * Get the user's tickets and entitlements
 * Includes ticket details and active entitlement progress
 * Requires authentication
 *
 * @param token - Firebase ID token
 */
export async function getTickets(token: string): Promise<JackpotTickets> {
  const response = await fetch(buildCloudApiUrl(ENDPOINTS.tickets), {
    method: 'GET',
    headers: getAuthHeaders(token),
  });
  return handleResponse<JackpotTickets>(response);
}

/**
 * Purchase tickets directly with tokens
 * Price: 500 tokens per ticket
 * Requires authentication
 *
 * @param token - Firebase ID token
 * @param quantity - Number of tickets to purchase (1-100)
 */
export async function purchaseTickets(
  token: string,
  quantity: number
): Promise<TicketPurchaseResponse> {
  const response = await fetch(buildCloudApiUrl(ENDPOINTS.purchaseTickets), {
    method: 'POST',
    headers: getAuthHeaders(token),
    body: JSON.stringify({ quantity }),
  });
  return handleResponse<TicketPurchaseResponse>(response);
}

/**
 * Spin the jackpot wheel using a ticket
 * Uses cryptographic RNG for fair results
 * Requires authentication
 *
 * @param token - Firebase ID token
 * @param ticketId - Optional specific ticket ID to use (uses oldest if not specified)
 */
export async function spin(token: string, ticketId?: string): Promise<SpinResult> {
  const response = await fetch(buildCloudApiUrl(ENDPOINTS.spin), {
    method: 'POST',
    headers: getAuthHeaders(token),
    body: JSON.stringify({ ticket_id: ticketId }),
  });
  return handleResponse<SpinResult>(response);
}

/**
 * Spin all available tickets at once
 * Requires authentication
 *
 * @param token - Firebase ID token
 * @param maxTickets - Optional maximum number of tickets to spin
 */
export async function spinAll(token: string, maxTickets?: number): Promise<SpinAllResult> {
  const response = await fetch(buildCloudApiUrl(ENDPOINTS.spinAll), {
    method: 'POST',
    headers: getAuthHeaders(token),
    body: JSON.stringify({ max_tickets: maxTickets }),
  });
  return handleResponse<SpinAllResult>(response);
}

/**
 * Grouped export for convenient importing
 */
export const jackpotApi = {
  getPool,
  checkin,
  getTickets,
  purchaseTickets,
  spin,
  spinAll,
};
